// HubPageGenerator.js - Builds the pages that lead to the guides: /guides/index.html (all countries and cities)
// and /guides/<country>/index.html (one per country). Both work from the guide list alone (Name, Locality,
// Country, Description, Photo, pageUrl), so "Regenerate Index" can build them without loading every guide.
// Same look as the city pages: public/city-page.css (prefix lx-), behaviour in public/city-page.js.
import { SITE_BASE, escapeHtml as e, createSlug, getCountryName, entryUrl, cityKeyOf, placeName, cityPageLabel } from './guidesSite';
import { CITY_CONTENT } from '../../data/cityPages';
import { IOS_URL, GOOGLE_TAG, stores, shorten } from './pageParts';

// Where the pins of the index globe go: city pages by their folder name, countries by theirs.
// A city or country missing here simply gets no pin (its card is still on the page); add it when it appears.
const CITY_COORDS = {
  'lisbon': [38.72, -9.14], 'porto': [41.15, -8.61], 'sintra': [38.80, -9.38], 'cascais': [38.70, -9.42],
  'leiria': [39.74, -8.81], 'batalha': [39.66, -8.82], 'tomar': [39.60, -8.41], 'funchal': [32.65, -16.91],
  'coimbra': [40.21, -8.43], 'evora': [38.57, -7.91], 'faro': [37.02, -7.93], 'braga': [41.55, -8.42], 'obidos': [39.36, -9.16],
  'rio-de-janeiro': [-22.91, -43.17], 'sao-paulo': [-23.55, -46.63], 'salvador': [-12.97, -38.50], 'brasilia': [-15.79, -47.88],
  'madrid': [40.42, -3.70], 'barcelona': [41.39, 2.17], 'granada': [37.18, -3.60], 'seville': [37.39, -5.98], 'sevilla': [37.39, -5.98],
  'toledo': [39.86, -4.02], 'valencia': [39.47, -0.38], 'sofia': [42.70, 23.32], 'plovdiv': [42.14, 24.75],
  'rome': [41.90, 12.50], 'florence': [43.77, 11.26], 'venice': [45.44, 12.32], 'paris': [48.86, 2.35], 'london': [51.51, -0.13],
  'edinburgh': [55.95, -3.19], 'new-york': [40.71, -74.01], 'dubai': [25.20, 55.27], 'zurich': [47.38, 8.54], 'geneva': [46.20, 6.14],
};
const COUNTRY_COORDS = {
  'portugal': [39.6, -8.0], 'brazil': [-14.2, -51.9], 'spain': [40.2, -3.7], 'bulgaria': [42.7, 25.5], 'italy': [42.8, 12.6],
  'switzerland': [46.8, 8.2], 'united-kingdom': [54.0, -2.5], 'united-states': [39.8, -98.6], 'united-arab-emirates': [24.0, 54.0],
  'france': [46.6, 2.4], 'germany': [51.1, 10.4], 'netherlands': [52.2, 5.3], 'belgium': [50.6, 4.6], 'austria': [47.6, 14.1],
  'czech-republic': [49.8, 15.5], 'hungary': [47.2, 19.4], 'poland': [52.0, 19.1], 'romania': [45.9, 24.9], 'greece': [39.1, 22.0],
  'turkey': [39.0, 35.2], 'ireland': [53.2, -8.0], 'ukraine': [48.4, 31.2], 'sweden': [62.0, 15.0], 'norway': [61.0, 9.0],
  'denmark': [56.0, 10.0], 'finland': [62.0, 26.0], 'israel': [31.0, 34.9], 'morocco': [31.8, -7.1], 'egypt': [26.8, 30.8],
  'mexico': [23.6, -102.5], 'canada': [56.0, -96.0], 'argentina': [-38.4, -63.6], 'chile': [-35.7, -71.5], 'peru': [-9.2, -75.0],
  'colombia': [4.6, -74.1], 'japan': [36.2, 138.3], 'china': [35.9, 104.2], 'south-korea': [36.5, 127.9], 'india': [20.6, 79.0],
  'thailand': [15.9, 100.9], 'singapore': [1.35, 103.8], 'indonesia': [-2.5, 118.0], 'australia': [-25.3, 133.8],
  'new-zealand': [-41.0, 174.0], 'south-africa': [-30.6, 22.9], 'kenya': [0.0, 37.9],
};

const coordsOf = (g) => {
  const lat = Number(g.Latitude), lng = Number(g.Longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? [lat, lng] : null;
};

// at most this many guides of one city on the globe: it is look and feel, not the full list
const GLOBE_GUIDES_PER_CITY = 3;
// a world sight this close to one of our pins is left out (our guide already marks the spot)
const GLOBE_SIGHT_GAP_KM = 2;
const kmBetween = (a, b) => {
  const r = Math.PI / 180, dLat = (b[0] - a[0]) * r, dLng = (b[1] - a[1]) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLng / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
};

/**
 * The globe pins, biggest first so the tour starts there:
 *  - a city pin (k 'city') for every city page and every other city with guides, with its number of guides (c);
 *    a city without its own page links to its country page
 *  - a guide pin (k 'guide'): up to GLOBE_GUIDES_PER_CITY guides of each city, linking to the guide
 *  - a sight pin (k 'sight') for the world's best-known places (public/globe/places.json, guide-studio/
 *    globe_places.py), so the globe looks covered everywhere; it links only to a city page we have, never claims a guide
 *  - a country pin only for a country whose guides have no position at all
 */
export const globePins = (countries, cityPages, guides = [], sights = []) => {
  const pins = [];
  const placed = named(guides).map(g => ({ g, at: coordsOf(g) }));
  const byCity = {};
  placed.forEach(x => { const k = cityKeyOf(x.g); (byCity[k] = byCity[k] || []).push(x); });
  const middle = (list) => {
    const pts = list.filter(x => x.at);
    return pts.length ? [pts.reduce((s, x) => s + x.at[0], 0) / pts.length, pts.reduce((s, x) => s + x.at[1], 0) / pts.length] : null;
  };
  const done = new Set();
  cityPages.forEach(c => {
    done.add(c.key);
    const at = CITY_COORDS[c.citySlug] || (c.center ? [c.center.lat, c.center.lng] : null) || middle(byCity[c.key] || []);
    if (at) pins.push({ n: c.city, d: `${c.country} · ${cityPageLabel(c)}`, u: c.url, lat: at[0], lng: at[1], k: 'city', c: c.count });
  });
  Object.entries(byCity).filter(([key]) => !done.has(key)).forEach(([key, list]) => {
    const at = middle(list);
    if (!at) return;
    const country = getCountryName(list[0].g.Country || '');
    pins.push({ n: placeName(list[0].g.Locality) || country, d: `${country} · ${plural(list.length, 'audio guide', 'audio guides')}`,
      u: `/guides/${key.split('/')[0]}/`, lat: at[0], lng: at[1], k: 'city', c: list.length });
  });
  pins.sort((a, b) => b.c - a.c);
  Object.values(byCity).forEach(list => list.filter(x => x.at).slice(0, GLOBE_GUIDES_PER_CITY).forEach(x =>
    pins.push({ n: String(x.g.Name).trim(), d: placeName(x.g.Locality) || getCountryName(x.g.Country || ''),
      u: entryUrl(x.g), lat: Number(x.at[0].toFixed(5)), lng: Number(x.at[1].toFixed(5)), k: 'guide' })));
  const ours = pins.map(p => [p.lat, p.lng]);
  sights.forEach(x => {
    if (!Number.isFinite(x.lat) || !Number.isFinite(x.lng) || ours.some(o => kmBetween(o, [x.lat, x.lng]) < GLOBE_SIGHT_GAP_KM)) return;
    const page = cityPages.find(c => c.city.toLowerCase() === String(x.city || '').toLowerCase());
    pins.push({ n: x.name, d: [x.city, x.country].filter(Boolean).join(', '), u: page ? page.url : '', lat: x.lat, lng: x.lng, k: 'sight' });
  });
  // a country whose guides all lack a position still gets a pin, where the country is
  const withPins = new Set(pins.filter(p => p.u).map(p => p.u.split('/')[2]));
  countries.filter(k => !withPins.has(k.countrySlug)).forEach(k => {
    const at = COUNTRY_COORDS[k.countrySlug];
    if (at) pins.push({ n: k.country, d: plural(k.count, 'audio guide', 'audio guides'), u: k.url, lat: at[0], lng: at[1], k: 'country', c: k.count });
  });
  return pins;
};

// how many guides a city or country shows before its "see all" link
const PER_COUNTRY_ON_INDEX = 4;
const PER_CITY_ON_COUNTRY = 6;

const head = (title, em) => `${e(title)}<em>${e(em)}</em>`;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const named = (guides) => guides.filter(g => g.Name && String(g.Name).trim());
const slugOfUrl = (url) => (String(url).match(/\/([^/]+)\/[^/]+-seo\.html$/) || [])[1] || '';

/**
 * The photo a city is shown with: the city page's own hero image when it has a content file,
 * otherwise the first guide of the city that has a photo.
 */
export const cityPhoto = (city, guides) => {
  const inCity = named(guides).filter(g => cityKeyOf(g) === city.key);
  const ref = (CITY_CONTENT[city.key] || {}).heroImage;
  if (ref && ref.startsWith('guide:')) {
    const def = ((CITY_CONTENT[city.key] || {}).guides || []).find(d => d.key === ref.slice(6));
    const slugs = def ? Object.values(def.versions || {}) : [];
    const hit = inCity.find(g => slugs.includes(slugOfUrl(entryUrl(g))) && g.Photo);
    if (hit) return hit.Photo;
  } else if (ref) {
    return /^(https?:)?\//.test(ref) ? ref : city.url + ref;
  }
  const withPhoto = inCity.find(g => g.Photo);
  return withPhoto ? withPhoto.Photo : (city.photo || '');
};

/**
 * One page per country that has guides. Returns [{ country, countrySlug, url, count, cities, places, photo }],
 * biggest first. `cities` are the city pages in that country, `places` every town or city with a guide.
 */
export const findCountryPages = (guides, cityPages = []) => {
  const byCountry = {};
  named(guides).forEach(g => {
    const country = getCountryName(g.Country || '');
    (byCountry[country] = byCountry[country] || []).push(g);
  });
  return Object.entries(byCountry).map(([country, list]) => {
    const countrySlug = createSlug(country);
    const cities = cityPages.filter(c => c.countrySlug === countrySlug);
    const places = [...new Set(list.map(g => placeName(g.Locality)).filter(Boolean))];
    const photo = (cities[0] && cityPhoto(cities[0], guides)) || (list.find(g => g.Photo) || {}).Photo || '';
    return { country, countrySlug, url: `/guides/${countrySlug}/`, count: list.length, cities, places, photo, guides: list };
  }).sort((a, b) => b.count - a.count);
};

// ─── HTML pieces ─────────────────────────────────────────────────────────────

const img = (src, alt = '', w = 640, h = 400) => src
  ? `<img src="${e(src)}" alt="${e(alt)}" loading="lazy" decoding="async" width="${w}" height="${h}">`
  : '';

/** A guide card: photo, place, title, a short description. */
const guideCard = (g) => {
  const url = entryUrl(g);
  const place = placeName(g.Locality);
  return `<article class="lx-card">`
    + `<a class="lx-card-img" href="${e(url)}" tabindex="-1" aria-hidden="true">${img(g.Photo)}</a>`
    + `<div class="lx-card-body">${place ? `<div class="lx-card-meta"><span>${e(place)}</span></div>` : ''}`
    + `<h3><a href="${e(url)}">${e(String(g.Name).trim())}</a></h3>${g.Description ? `<p>${e(shorten(g.Description, 120))}</p>` : ''}`
    + `<div class="lx-card-foot"><span></span><a class="lx-open" href="${e(url)}">Open guide <span aria-hidden="true">→</span></a></div>`
    + `</div></article>`;
};

/** A big photo card for a city page. */
const placeCard = (c, guides, showCountry = true) =>
  `<a class="lx-place" href="${e(c.url)}">${img(cityPhoto(c, guides), '', 800, 600)}`
  + `<span class="lx-place-in">${showCountry ? `<span class="lx-place-kicker">${e(c.country)}</span>` : ''}`
  + `<strong>${e(c.city)}</strong><span class="lx-place-more">${e(cityPageLabel(c))} <span aria-hidden="true">→</span></span></span></a>`;

/** A country card: photo, name, numbers, its city pages as chips. */
const countryCard = (k) =>
  `<article class="lx-country"><a class="lx-country-img" href="${e(k.url)}" tabindex="-1" aria-hidden="true">${img(k.photo)}</a>`
  + `<div class="lx-country-body"><h3><a href="${e(k.url)}">${e(k.country)}</a></h3>`
  + `<p>${plural(k.count, 'guide', 'guides')} · ${plural(k.places.length, 'place', 'places')}</p>`
  + (k.cities.length ? `<div class="lx-chips">${k.cities.map(c => `<a href="${e(c.url)}">${e(c.city)}</a>`).join('')}</div>` : '')
  + `<a class="lx-open" href="${e(k.url)}">All ${e(k.country)} guides <span aria-hidden="true">→</span></a></div></article>`;

const ld = (items) => items.map(x => `  <script type="application/ld+json">${JSON.stringify(x).replace(/</g, '\\u003c')}</script>`).join('\n');

const page = ({ title, description, url, image, root, ldItems, body, extraHead = '', extraScripts = '' }) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${e(title)}</title>
  <meta name="description" content="${e(description)}">
  <link rel="canonical" href="${url}">
  <meta name="robots" content="index, follow, max-image-preview:large">
  <meta property="og:type" content="website">
  <meta property="og:site_name" content="GuideSofia">
  <meta property="og:url" content="${url}">
  <meta property="og:title" content="${e(title)}">
  <meta property="og:description" content="${e(description)}">
  ${image ? `<meta property="og:image" content="${e(/^https?:/.test(image) ? image : SITE_BASE + image)}">` : ''}
  <meta name="twitter:card" content="summary_large_image">
  <meta name="apple-itunes-app" content="app-id=6748287909">
${ld(ldItems)}
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css">
  <link rel="stylesheet" href="${root}seo-styles.css">
  <link rel="stylesheet" href="${root}city-page.css">
${extraHead}  <script async src="https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG}"></script>
  <script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GOOGLE_TAG}');</script>
</head>
<body>
<div id="header-container"></div>
${body}
<div id="footer-container"></div>
<script src="${root}city-page.js"></script>
<script src="${root}seo-scripts.js"></script>
${extraScripts}</body>
</html>
`;

const ctaAndB2b = (where, place, kind) => `
  <section class="lx-cta">
    <div class="lx-cta-in">
      <h2>${head(`Take ${where} `, 'in your pocket')}</h2>
      <p>Download GuideSofia, pick a guide and start walking. It's free to start your first tour.</p>
      ${stores('cta', true, kind)}
    </div>
  </section>

  <aside class="lx-b2b">
    <div><p class="lx-kicker">For museums &amp; cities</p>
    <h2>Run a museum or heritage site${place ? ` in ${e(place)}` : ''}?</h2>
    <p>We turn the texts you already have into an audio guide on the visitor's own phone, approved by your team.</p></div>
    <div class="lx-b2b-links"><a class="lx-btn" href="https://www.guidesofia.com/business/#demo" data-gs-event="demo_click_${kind}">Book a 15-minute demo</a><a class="lx-btn-ghost" href="https://www.guidesofia.com/business/">How it works</a></div>
  </aside>`;

const faqHtml = (items) => items.map(([q, a], i) => `<details class="lx-faq-item"${i === 0 ? ' open' : ''}><summary>${e(q)}</summary><p>${e(a)}</p></details>`).join('');
const faqLd = (items) => ({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: items.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })) });
const listSentence = (names) => names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] || '');

// ─── /guides/index.html ──────────────────────────────────────────────────────

export const buildIndexHtml = (guides, cityPages = [], sights = []) => {
  const all = named(guides);
  const N = all.length;
  const countries = findCountryPages(all, cityPages);
  const C = countries.length;
  const heroImage = (cityPages[0] && cityPhoto(cityPages[0], all)) || (countries[0] && countries[0].photo) || '';

  // id="<country>": pages generated before the country pages existed link to /guides/#<country>
  const byCountry = countries.map(k => `
      <div class="lx-group-head" id="${e(k.countrySlug)}"><h3><a href="${e(k.url)}">${e(k.country)}</a></h3><span>${plural(k.count, 'guide', 'guides')}</span>`
    + `${k.count > PER_COUNTRY_ON_INDEX ? `<a class="lx-open" href="${e(k.url)}">See all ${k.count} <span aria-hidden="true">→</span></a>` : ''}</div>
      <div class="lx-grid">${k.guides.slice(0, PER_COUNTRY_ON_INDEX).map(guideCard).join('')}</div>`).join('');

  const faq = [
    ['What are GuideSofia audio guides?', `Self-guided audio tours you play on your own phone in the GuideSofia app. There are ${N} of them in ${plural(C, 'country', 'countries')}, from city walks to single museums and monuments.`],
    ['Do they work offline?', 'Yes. Download your itinerary and audio guides over Wi-Fi, then use them outdoors with GPS without spending mobile data.'],
    ['Is it free?', "The GuideSofia app is free to download, and it's free to start your first tour."],
    ['Which languages?', 'The pages here are in English. In the app, every guide is narrated in 30+ languages.'],
    ['Can I ask the guide questions?', 'Yes. Open the chat in the app and ask about what you are looking at, local history, food or nearby places.'],
  ];

  const url = `${SITE_BASE}/guides/`;
  const title = `Audio Guides: ${N} Self-Guided Tours in ${C} ${C === 1 ? 'Country' : 'Countries'} | GuideSofia`;
  const description = `${N} self-guided audio tours: ${listSentence(cityPages.slice(0, 4).map(c => c.city))} and more. Play them on your phone, walk at your own pace. Works offline.`;
  const ldItems = [
    { '@context': 'https://schema.org', '@type': 'CollectionPage', name: 'GuideSofia audio guides', url, inLanguage: 'en',
      dateModified: new Date().toISOString().split('T')[0],
      publisher: { '@type': 'Organization', name: 'GuideSofia', url: 'https://www.guidesofia.com/' },
      hasPart: [...cityPages.map(c => ({ '@type': 'CollectionPage', name: `${c.city} ${c.count ? 'audio guides' : 'travel guide'}`, url: SITE_BASE + c.url })),
        ...countries.map(k => ({ '@type': 'CollectionPage', name: `${k.country} audio guides`, url: SITE_BASE + k.url }))] },
    faqLd(faq),
  ];

  const pins = globePins(countries, cityPages, all, sights);
  const nCities = pins.filter(p => p.k === 'city').length;
  const body = `<main class="lx lx-hub" id="main" data-page="index">
  <header class="lx-hero lx-hero-hub lx-hero-globe">
    <div class="lx-globe" data-pins="${e(JSON.stringify(pins))}"><canvas aria-label="Globe with every city and guide we have: zoom in to see each guide, click to open it" role="img"></canvas><a class="lx-globe-label" href="/guides/"></a></div>
    <div class="lx-globe-ui">
      <span class="lx-globe-key"><i class="is-city"></i>${plural(nCities, 'city', 'cities')} with audio guides<i class="is-guide"></i>our guides<i class="is-sight"></i>more to explore</span>
      <span class="lx-globe-zoom"><button type="button" data-zoom="in" aria-label="Zoom in">+</button><button type="button" data-zoom="out" aria-label="Zoom out">−</button><button type="button" data-zoom="reset" aria-label="Show the whole world" title="Whole world">⟲</button></span>
    </div>
    <div class="lx-hero-in">
      <p class="lx-eyebrow">Self-guided audio tours · ${plural(C, 'country', 'countries')}</p>
      <h1>Audio guides<em>Hear the place where it happened.</em></h1>
      <p class="lx-lead">${N} self-guided audio tours for cities, museums and monuments. Play them on your phone, walk at your own pace, and ask the guide anything along the way.</p>
      <form class="lx-search" role="search" action="#" onsubmit="return false">
        <label class="lx-search-box"><svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M10 3a7 7 0 0 1 5.6 11.2l4.6 4.6-1.4 1.4-4.6-4.6A7 7 0 1 1 10 3zm0 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10z"/></svg>
        <input type="search" id="lx-q" placeholder="Search a city, museum or monument" aria-label="Search the audio guides" autocomplete="off"></label>
      </form>
      <dl class="lx-stats">
        <div><dt>Guides</dt><dd>${N}</dd></div>
        <div><dt>Countries</dt><dd>${C}</dd></div>
        <div><dt>City guides</dt><dd>${cityPages.length}</dd></div>
        <div><dt>Offline</dt><dd>Yes</dd></div>
      </dl>
    </div>
  </header>

  <section class="lx-sec lx-results-sec" id="results" hidden>
    <div class="lx-head"><p class="lx-kicker">Search</p><h2 id="lx-results-title">Results</h2></div>
    <div class="lx-grid" id="lx-results"></div>
  </section>
${cityPages.length ? `
  <section class="lx-sec" id="cities">
    <div class="lx-head"><p class="lx-kicker">City guides</p><h2>${head('Start with ', 'a city')}</h2>
    <p>Every guide for the city on one page, by theme and neighbourhood, with samples you can play first.</p></div>
    <div class="lx-places">${cityPages.map(c => placeCard(c, all)).join('')}</div>
  </section>` : ''}

  <section class="lx-sec lx-sec-paper" id="countries">
    <div class="lx-head"><p class="lx-kicker">Countries</p><h2>${head('Browse by ', 'country')}</h2></div>
    <div class="lx-countries">${countries.map(countryCard).join('')}</div>
  </section>

  <section class="lx-sec" id="guides">
    <div class="lx-head"><p class="lx-kicker">Guides</p><h2>${head('A few from ', 'each country')}</h2>
    <p>Search above to find any of the ${N} guides, or open a country to see them all.</p></div>
    ${byCountry}
  </section>

  <section class="lx-sec lx-sec-paper" id="faq">
    <div class="lx-head"><p class="lx-kicker">FAQ</p><h2>${head('Questions about ', 'the audio guides')}</h2></div>
    <div class="lx-faq">${faqHtml(faq)}</div>
  </section>
${ctaAndB2b('the guide', '', 'index')}

  <div class="lx-bar" aria-hidden="true"><span>${N} audio guides</span><a href="${IOS_URL}" data-store>Get the app</a></div>
</main>`;

  return page({ title, description, url, image: heroImage, root: '', ldItems, body,
    extraHead: '  <script src="guides-registry.js"></script>\n', extraScripts: '<script src="guides-globe.js"></script>\n' });
};

// ─── /guides/<country>/index.html ────────────────────────────────────────────

/**
 * @param {Object} country - from findCountryPages
 * @param {Array} guides   - the whole guide list (for city photos)
 */
export function generateCountryPage(country, guides) {
  const name = country.country;
  const list = country.guides;
  const N = list.length;
  const pageUrl = SITE_BASE + country.url;

  // groups: each city page first (a few guides and a link), then places with two or more guides, then the rest
  const byPlace = {};
  list.forEach(g => { const p = placeName(g.Locality) || 'Other'; (byPlace[p] = byPlace[p] || []).push(g); });
  const cityOf = (g) => country.cities.find(c => c.key === cityKeyOf(g));
  const groups = [];
  country.cities.forEach(c => {
    const items = list.filter(g => cityOf(g) === c);
    if (items.length) groups.push({ key: c.citySlug, title: c.city, city: c, items });
  });
  const inCityPage = new Set(groups.flatMap(gr => gr.items));
  const rest = Object.entries(byPlace)
    .map(([p, items]) => [p, items.filter(g => !inCityPage.has(g))])
    .filter(([, items]) => items.length)
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  const singles = [];
  rest.forEach(([p, items]) => {
    if (items.length >= 2) groups.push({ key: createSlug(p), title: p, items });
    else singles.push(...items);
  });
  if (singles.length) groups.push({ key: 'more', title: groups.length ? `More places in ${name}` : `Every ${name} guide`, items: singles });

  const filters = groups.length > 1
    ? `<div class="lx-filters" role="toolbar" aria-label="Filter by place"><button type="button" class="is-on" data-filter="all">All places <small>${N}</small></button>`
      + groups.map(gr => `<button type="button" data-filter="${e(gr.key)}">${e(gr.key === 'more' ? 'More places' : gr.title)} <small>${gr.items.length}</small></button>`).join('') + `</div>`
    : '';
  const grid = groups.map(gr => {
    const shown = gr.city ? gr.items.slice(0, PER_CITY_ON_COUNTRY) : gr.items;
    const more = gr.city ? `<a class="lx-open" href="${e(gr.city.url)}">${gr.items.length > shown.length ? `All ${gr.items.length} on the ${e(gr.city.city)} page` : `The ${e(gr.city.city)} page`} <span aria-hidden="true">→</span></a>` : '';
    return `<div class="lx-group-head" data-group="${e(gr.key)}"><h3>${gr.city ? `<a href="${e(gr.city.url)}">${e(gr.title)}</a>` : e(gr.title)}</h3><span>${plural(gr.items.length, 'guide', 'guides')}</span>${more}</div>`
      + `<div class="lx-grid" data-group="${e(gr.key)}">${shown.map(guideCard).join('')}</div>`;
  }).join('');

  const placesList = country.places.slice(0, 6);
  const faq = [
    [`Is there an audio guide for ${name}?`, `Yes. GuideSofia has ${plural(N, 'self-guided audio tour', 'self-guided audio tours')} in ${name}${country.places.length > 1 ? `, in ${listSentence(placesList)}${country.places.length > placesList.length ? ' and more' : ''}` : country.places[0] ? ` (${country.places[0]})` : ''}. You play them on your own phone in the GuideSofia app and walk at your own pace.`],
    ...(country.cities.length ? [[`Which ${name} cities have their own page?`, `${listSentence(country.cities.map(c => c.city))}: every guide for the city on one page, by theme, with samples you can play first.`]] : []),
    ['Do the guides work offline?', 'Yes. Download your itinerary and audio guides over Wi-Fi, then use them outdoors with GPS without spending mobile data.'],
    ['Is it free?', "The GuideSofia app is free to download, and it's free to start your first tour."],
    ['Which languages?', 'The pages here are in English. In the app, every guide is narrated in 30+ languages.'],
  ];

  const title = `${name} Audio Guides: ${plural(N, 'Self-Guided Tour', 'Self-Guided Tours')} | GuideSofia`;
  const description = `${plural(N, 'self-guided audio tour', 'self-guided audio tours')} in ${name}${placesList.length > 1 ? `: ${listSentence(placesList.slice(0, 4))} and more` : ''}. Play them on your phone, walk at your own pace. Works offline.`;
  const ldItems = [
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Audio guides', item: `${SITE_BASE}/guides/` },
      { '@type': 'ListItem', position: 2, name, item: pageUrl }] },
    { '@context': 'https://schema.org', '@type': 'CollectionPage', name: `${name} audio guides`, url: pageUrl, inLanguage: 'en',
      dateModified: new Date().toISOString().split('T')[0],
      about: { '@type': 'Country', name },
      publisher: { '@type': 'Organization', name: 'GuideSofia', url: 'https://www.guidesofia.com/' },
      mainEntity: { '@type': 'ItemList', numberOfItems: N, itemListElement: list.map((g, i) => ({
        '@type': 'ListItem', position: i + 1, item: { '@type': 'TouristTrip', name: String(g.Name).trim(), url: SITE_BASE + entryUrl(g) } })) } },
    faqLd(faq),
  ];

  const body = `<main class="lx lx-hub" id="main" data-page="country">
  <header class="lx-hero lx-hero-hub">
    ${country.photo ? `<img class="lx-hero-bg" src="${e(country.photo)}" alt="" fetchpriority="high">` : ''}
    <div class="lx-hero-in">
      <nav class="lx-crumbs" aria-label="Breadcrumb"><a href="/guides/">Audio guides</a><span>/</span><b aria-current="page">${e(name)}</b></nav>
      <p class="lx-eyebrow">${e(name)} · Self-guided audio tours</p>
      <h1>${e(name)} audio guides</h1>
      <p class="lx-lead">${plural(N, 'self-guided audio tour', 'self-guided audio tours')} in ${plural(country.places.length, 'place', 'places')} across ${e(name)}. Play them on your phone, walk at your own pace, and ask the guide anything along the way.</p>
      <div class="lx-hero-actions">${stores('hero', false, 'country')}</div>
      <dl class="lx-stats">
        <div><dt>Guides</dt><dd>${N}</dd></div>
        <div><dt>Places</dt><dd>${country.places.length}</dd></div>
        ${country.cities.length ? `<div><dt>City guides</dt><dd>${country.cities.length}</dd></div>` : ''}
        <div><dt>Offline</dt><dd>Yes</dd></div>
      </dl>
    </div>
  </header>
${country.cities.length ? `
  <section class="lx-sec" id="cities">
    <div class="lx-head"><p class="lx-kicker">City guides</p><h2>${head(`${name}, `, 'city by city')}</h2>
    <p>Every guide for the city on one page, by theme and neighbourhood, with samples you can play first.</p></div>
    <div class="lx-places${country.cities.length === 1 ? ' lx-places-one' : ''}">${country.cities.map(c => placeCard(c, guides, false)).join('')}</div>
  </section>` : ''}

  <section class="lx-sec lx-sec-paper" id="guides">
    <div class="lx-head"><p class="lx-kicker">All guides</p><h2>${head(`Every ${name} guide`, groups.length > 1 ? ', by place' : '')}</h2></div>
    ${filters}
    ${grid}
  </section>

  <section class="lx-sec" id="faq">
    <div class="lx-head"><p class="lx-kicker">FAQ</p><h2>${head('Questions about ', `${name} audio guides`)}</h2></div>
    <div class="lx-faq">${faqHtml(faq)}</div>
  </section>
${ctaAndB2b(name, name, 'country')}

  <div class="lx-bar" aria-hidden="true"><span>${e(name)} · ${plural(N, 'audio guide', 'audio guides')}</span><a href="${IOS_URL}" data-store>Get the app</a></div>
</main>`;

  return page({ title, description, url: pageUrl, image: country.photo, root: '../', ldItems, body });
}
